import { FormattedMessage, FormattedDate } from 'react-intl';
import classNames from 'classnames';
import { connect } from 'react-redux';

import { Tooltip } from '../../../ui/tooltip/Tooltip';
import { TooltipQuestionMarkSVG } from '../../../../svgComponents/tooltipQuestionMarkSVG/TooltipQuestionMarkSVG';
import { MoneyWithDecimals } from '../../../ui/moneyWithDecimals/index';
import {
    getMaxAmountCreditAvailable,
    hasLoanBeenNewlyFundedWithoutRepayments,
    hasLoanBeenFundedWithRepaymentsAndExpiredApproval,
    getLatestFundingCashflowDate,
    getDrawnDownLoanAmount,
    hasApprovalExpired,
    hasAvailableCash,
    getCreditLimit,
    getProductTypeFromURL,
    isTopUpPending
} from '../../accountsSelector';
import messages from '../OutstandingView.messages';
import { FormattedCurrency } from '../../../../iwIntlProvider/FormattedCurrency';
import styles from './CreditLimit.module.css';
import { RootState } from '@iwProvider/configureStore';

const mapStateToProps = (state: RootState) => {
    const productType = getProductTypeFromURL();
    return {
        maxAmountCreditAvailable: getMaxAmountCreditAvailable(
            state,
            productType
        ),
        creditLimit: getCreditLimit(state, productType),
        isNewlyFundedPartialLoanWithoutRepayment:
            hasLoanBeenNewlyFundedWithoutRepayments(state, productType),
        isFundedPartialLoanWithRepayments:
            hasLoanBeenFundedWithRepaymentsAndExpiredApproval(state),
        drawDownDate: getLatestFundingCashflowDate(state, productType),
        drawDownLoanAmount: getDrawnDownLoanAmount(state, productType),
        hasApprovalExpired: hasApprovalExpired(state, productType),
        hasAvailableCash: hasAvailableCash(state, productType),
        isTopupPending: isTopUpPending(state, productType)
    };
};

type PropsFromRedux = ReturnType<typeof mapStateToProps>;

const CreditLimit = ({
    maxAmountCreditAvailable,
    creditLimit,
    isNewlyFundedPartialLoanWithoutRepayment,
    isFundedPartialLoanWithRepayments,
    drawDownDate,
    drawDownLoanAmount,
    hasApprovalExpired,
    hasAvailableCash,
    isTopupPending
}: PropsFromRedux) => {
    const isTopUpApproved = !hasApprovalExpired && hasAvailableCash;

    const creditLimitStyles = classNames(
        !hasApprovalExpired &&
            hasAvailableCash &&
            !isTopupPending &&
            styles.green,
        isNewlyFundedPartialLoanWithoutRepayment &&
            !hasAvailableCash &&
            styles.grey,
        isFundedPartialLoanWithRepayments && styles.grey
    );

    const creditLimitMoneyStyles = classNames(
        !hasApprovalExpired &&
            hasAvailableCash &&
            !isTopupPending &&
            styles.green,
        isNewlyFundedPartialLoanWithoutRepayment &&
            !hasAvailableCash &&
            styles.grey,
        isFundedPartialLoanWithRepayments && styles.grey,
        hasApprovalExpired && styles.grey
    );

    return (
        <div className={styles.creditLimit}>
            <div
                className={classNames(styles.title, creditLimitStyles)}
                data-testid="creditLimit"
            >
                <Tooltip
                    className={styles.tooltip}
                    message={
                        <FormattedMessage
                            {...messages.creditLimitToolTipMessage}
                            values={{
                                loanAmount: (
                                    <FormattedCurrency
                                        value={drawDownLoanAmount}
                                    />
                                ),
                                creditLimit: (
                                    <FormattedCurrency value={creditLimit} />
                                ),
                                drawDownDate: drawDownDate ? (
                                    <FormattedDate
                                        value={new Date(drawDownDate)}
                                    />
                                ) : (
                                    '-'
                                )
                            }}
                        />
                    }
                >
                    <TooltipQuestionMarkSVG className={`${styles.icon}`} />
                </Tooltip>
                {isTopUpApproved ? (
                    <FormattedMessage {...messages.creditLimitApproved} />
                ) : (
                    <FormattedMessage {...messages.creditLimit} />
                )}
            </div>
            <div
                className={classNames(styles.money, creditLimitMoneyStyles)}
                data-testid="creditLimit-money"
            >
                <MoneyWithDecimals value={maxAmountCreditAvailable} />
            </div>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(CreditLimit);
